/**
 * Collapse a raw landing-page URL (as captured in lead_source.metadata.entire_url
 * or lead_metadata[entire_url]) into a short, groupable label.
 *
 * Two things happen:
 *   1. The hostname + path is preserved (lower-cased, www stripped, trailing
 *      slash removed) so e.g. `https://www.practice.com/contact/?…` and
 *      `https://practice.com/contact?…` collapse to the same key.
 *   2. The first attribution marker found in the query string (gad_source,
 *      gclid, fbclid, msclkid, ttclid, twclid, or utm_source) is appended as
 *      a `(Channel)` suffix so a row like
 *        `practice.com/contact (Google Ads)`
 *      tells you it came from a paid Google click rather than just the page.
 *
 * Rationale: real-world entire_url values are noisy — Google Ads URLs alone
 * carry 6+ params, and grouping the raw string yields 100+ near-identical
 * "rows" that are useless for analysis. The normalised key keeps the page
 * AND the channel without the noise.
 *
 * Invalid / non-URL strings are returned (truncated) as-is so the caller can
 * still surface them in the table.
 */

const CHANNEL_RULES = [
    // Direct click-IDs win over utm_source — they're the strongest signal.
    { test: /(^|[?&])gad_source=/i, label: 'Google Ads' },
    { test: /(^|[?&])gclid=/i, label: 'Google Ads' },
    { test: /(^|[?&])fbclid=/i, label: 'Facebook' },
    { test: /(^|[?&])msclkid=/i, label: 'Bing Ads' },
    { test: /(^|[?&])ttclid=/i, label: 'TikTok' },
    { test: /(^|[?&])twclid=/i, label: 'Twitter / X' },
    { test: /(^|[?&])li_fat_id=/i, label: 'LinkedIn' },
    // Fall back to utm_source when no click-ID is present.
    { test: /(^|[?&])utm_source=google/i, label: 'Google' },
    { test: /(^|[?&])utm_source=facebook/i, label: 'Facebook' },
    { test: /(^|[?&])utm_source=instagram/i, label: 'Instagram' },
    { test: /(^|[?&])utm_source=bing/i, label: 'Bing' },
    { test: /(^|[?&])utm_source=tiktok/i, label: 'TikTok' },
    { test: /(^|[?&])utm_source=linkedin/i, label: 'LinkedIn' },
    { test: /(^|[?&])utm_source=youtube/i, label: 'YouTube' },
];

export function detectLandingUrlChannel(raw) {
    if (!raw || typeof raw !== 'string') return '';
    const match = CHANNEL_RULES.find((r) => r.test.test(raw));
    return match ? match.label : '';
}

export function normalizeLandingUrl(raw) {
    if (!raw || typeof raw !== 'string') return '';
    const trimmed = raw.trim();
    if (!trimmed) return '';
    const channel = detectLandingUrlChannel(trimmed);

    let base = trimmed;
    try {
        const u = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
        const host = u.hostname.replace(/^www\./i, '').toLowerCase();
        const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
        base = `${host}${path}`;
    } catch {
        // Not a parseable URL — strip from the first `?` and lower-case the host-ish prefix.
        const cut = trimmed.split('?')[0];
        base = cut.length > 80 ? `${cut.slice(0, 80)}…` : cut;
    }

    return channel ? `${base} (${channel})` : base;
}
